/**
 * Capabilities API service for fetching user plan information and capabilities.
 * Used for frontend feature gating.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

/**
 * User capabilities response from the API
 */
export interface PlanUsage {
  recordingsToday: number;
}

export interface UserCapabilities {
  userId: string;
  planId: string;
  capabilities: string[];
  limits: PlanLimits;
  usage?: PlanUsage;
}

/**
 * Plan usage limits
 */
export interface PlanLimits {
  recordingsPerDay: number;
  maxRecordingMinutes: number;
  cloudStorageGb: number;
  trialDays: number;
}

/**
 * Known capability identifiers
 */
export type Capability =
  | 'record'
  | 'apply_template'
  | 'create_project'
  | 'insight_recommendations'
  | 'sync_to_cloud'
  | 'original_transcript_download';

/**
 * Plan identifiers
 */
export type PlanId = 'free' | 'starter' | 'pro';

class CapabilitiesApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'CapabilitiesApi');
  }

  /**
   * Fetch user capabilities from the API
   */
  async getUserCapabilities(identityId?: string): Promise<UserCapabilities> {
    if (!this.baseUrl) {
      this.log.warn('Capabilities API URL not configured');
      return this.getDefaultCapabilities();
    }

    try {
      const headers = await this.getHeaders();
      const params = identityId ? `?identityId=${encodeURIComponent(identityId)}` : '';
      const response = await fetch(`${this.baseUrl}/user/capabilities${params}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.log.debug('User not authenticated, returning guest capabilities');
          return this.getDefaultCapabilities();
        }
        throw new Error(`Failed to fetch capabilities: ${response.statusText}`);
      }

      const data = await response.json();
      this.log.debug('Fetched user capabilities', { planId: data.planId });
      return data;
    } catch (error) {
      this.log.error('Failed to fetch capabilities', error instanceof Error ? error : new Error(String(error)));
      return this.getDefaultCapabilities();
    }
  }

  /**
   * Get default capabilities for unauthenticated users or when API is unavailable
   */
  private getDefaultCapabilities(): UserCapabilities {
    return {
      userId: '',
      planId: 'free',
      capabilities: ['record', 'apply_template', 'create_project'],
      limits: {
        recordingsPerDay: 3,
        maxRecordingMinutes: 30,
        cloudStorageGb: 0,
        trialDays: 30,
      },
    };
  }

  /**
   * Check if a specific capability is included
   */
  hasCapability(capabilities: string[], capability: Capability): boolean {
    return capabilities.includes(capability);
  }
}

export const capabilitiesApi = new CapabilitiesApiService();
export default capabilitiesApi;
